import { Icon, Input, Label, Select } from "@sagot/ui";
import { useEffect, useId, useRef, useState, type SyntheticEvent } from "react";
import { WORKFLOW_VALUE_MAX_CHARS, type WorkflowChoice, type WorkflowField, type WorkflowStepPayload } from "../workflow/contract";
import { workflowIssueText, type WorkflowLabels } from "../workflow/labels";
import { validateWorkflowValue, workflowInputTraits, type WorkflowValueIssue } from "../workflow/step";
import { primaryActionClass, useChatTheme } from "./chat-theme";

const CONTROL_HEIGHT = "h-11";

export interface WorkflowCollectFieldProps {
  card: WorkflowStepPayload;
  field: WorkflowField;
  labels: WorkflowLabels;
  disabled: boolean;
  describedBy: string | undefined;
  onAnswer: (card: WorkflowStepPayload, choice: WorkflowChoice) => void;
}

/** The message under the field: our own check first, else the server's "invalid" re-ask. */
function messageFor(issue: WorkflowValueIssue | null, serverInvalid: boolean, labels: WorkflowLabels): string {
  if (issue) return workflowIssueText(labels, issue);
  return serverInvalid ? labels.invalidFromServer : "";
}

export interface ConsentNoticeProps {
  id: string;
  text: string;
  url: string | undefined;
  linkLabel: string;
}

/** The RA 10173 notice of a card that asks for personal data: shown with the field, not a checkbox (the visitor agrees by answering). */
export function ConsentNotice({ id, text, url, linkLabel }: ConsentNoticeProps) {
  return (
    <p id={id} data-workflow-consent className="mt-2 flex items-start gap-1.5 text-xs leading-snug text-fg-muted">
      <Icon name="shield-check" className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" />
      <span className="min-w-0 break-words">
        {text}
        {url ? (
          <>
            {" "}
            <a href={url} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary-ink underline">
              {linkLabel}
            </a>
          </>
        ) : null}
      </span>
    </p>
  );
}

/** One labelled field with inline validation, Continue (and Skip when optional). A card that asks for personal data also shows its privacy notice. */
export function WorkflowCollectField({ card, field, labels, disabled, describedBy, onAnswer }: WorkflowCollectFieldProps) {
  const { themed } = useChatTheme();
  const id = useId();
  const controlRef = useRef<HTMLElement | null>(null);
  const bindControl = (element: HTMLElement | null) => {
    controlRef.current = element;
  };
  const [value, setValue] = useState("");
  const [issue, setIssue] = useState<WorkflowValueIssue | null>(null);
  const [edited, setEdited] = useState(false);
  const message = messageFor(issue, card.error === "invalid" && !edited, labels);
  const errorId = `${id}-error`;
  const consentId = `${id}-consent`;
  const consent = card.consent_text;

  // The server asked again after an invalid value: put the cursor back where the visitor fixes it.
  useEffect(() => {
    if (card.error === "invalid") controlRef.current?.focus();
  }, [card.error]);

  const change = (next: string) => {
    setValue(next);
    setEdited(true);
    setIssue(null);
  };
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const found = validateWorkflowValue(field, value);
    if (found) {
      setIssue(found);
      controlRef.current?.focus();
      return;
    }
    onAnswer(card, { value });
  };
  const shared = { id: `${id}-field`, "aria-describedby": [describedBy, errorId, consent ? consentId : undefined].filter(Boolean).join(" "), "aria-required": field.required || undefined, invalid: message !== "", disabled };

  return (
    <form noValidate onSubmit={submit} className="mt-2 space-y-3 rounded-lg border border-line bg-surface p-3">
      <div>
        <Label htmlFor={shared.id}>
          {field.label}
          {field.required ? (
            <span aria-hidden="true" className="text-danger">
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.type === "dropdown" ? (
          <Select ref={bindControl} {...shared} className={CONTROL_HEIGHT} value={value} onChange={(event) => { change(event.target.value); }}>
            <option value="">{labels.select}</option>
            {(field.options ?? []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
        ) : (
          <Input ref={bindControl} {...shared} {...workflowInputTraits(field.type)} className={CONTROL_HEIGHT} maxLength={WORKFLOW_VALUE_MAX_CHARS} value={value} onChange={(event) => { change(event.target.value); }} />
        )}
        <p id={errorId} aria-live="polite" className={message ? "mt-1.5 flex items-center gap-1 text-xs font-semibold text-danger" : "sr-only"}>
          {message ? <Icon name="alert-circle" className="size-3.5 shrink-0" /> : null}
          {message}
        </p>
        {consent ? <ConsentNotice id={consentId} text={consent} url={card.privacy_url} linkLabel={labels.privacyLink} /> : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={disabled} className={`${primaryActionClass(themed)} min-h-11 flex-1`}>
          {labels.submit}
        </button>
        {field.required ? null : (
          <button type="button" disabled={disabled} className="btn btn-ghost min-h-11" onClick={() => { onAnswer(card, { value: "" }); }}>
            {labels.skip}
          </button>
        )}
      </div>
    </form>
  );
}
